import React, { useEffect } from "react";
import _ from "lodash";
import { loadSessionStorageState } from "../../services/browserStorageService";
import { buildAddress } from "../Case/CasePdfGenerator/casePdfGeneratorService";
import PdfGeneratorFormElements from "../Case/CasePdfGenerator/PdfGeneratorFormElements";
import PdfGeneratorPreview from "../Case/CasePdfGenerator/PdfGeneratorPreview";
import casePaidTaskStyle from "./casePaidTaskPdfGeneratorStyle";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../../services/ReactQuery/reactQueryService";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import { getMediaObjectFilters } from "../../services/mediaObjectService";
import { useFormContext } from "react-hook-form-mui";
import { PaidTask } from "../../types/PaidTask";
import { Box } from "@mui/material";
import { useCurrentEditor } from "@tiptap/react";
import { getEditorHtml } from "../../services/Editor/editorFunctions";
import { ProductUnion } from "../../types/ProductUnion";

type CasePaidTaskPdfGeneratorProps = {
  product: ProductUnion;
  paidTask: PaidTask;
  initialValues: Object;
};

const CasePaidTaskPdfGenerator = ({ product, paidTask, initialValues }: CasePaidTaskPdfGeneratorProps) => {
  const currentUser = useCurrentUser();
  const formContext = useFormContext();
  const { editor } = useCurrentEditor();

  const mediaObjectsFilter = getMediaObjectFilters(product, currentUser);

  const { data: mediaObjects } = useQuery({
    queryKey: queryKeys.collection("media_objects", mediaObjectsFilter),
    queryFn: () => fetchCollection("media_objects", mediaObjectsFilter),
    enabled: !!product
  });

  const getParams = () => {
    const values = formContext.getValues();

    return _.merge({}, values, {
      content: getEditorHtml(editor),
      address: buildAddress(values, product),
      externalReference: !_.isEmpty(values.externalReference) ? values.externalReference : null,
      senderMedium: values.senderMedium === "none" ? null : values.senderMedium,
      externalLawyer: paidTask.assignee,
    });
  };

  useEffect(() => {
    const sessionStorageValues = loadSessionStorageState("customPdfValues" + product.id);
    const sessionStorageHtml = loadSessionStorageState("customPdfHtml" + product.id);
    if (sessionStorageHtml) {
      editor?.commands.setContent(sessionStorageHtml);
    }
    if (sessionStorageValues) {
      formContext.reset(
        _.merge(initialValues, {
          recipientType: sessionStorageValues.recipientType,
          address: {
            name: sessionStorageValues.address.name,
            department: sessionStorageValues.address.department,
            subDepartment: sessionStorageValues.address.subDepartment,
            streetAddress: sessionStorageValues.address.streetAddress,
            postalCode: sessionStorageValues.address.postalCode,
            addressLocality: sessionStorageValues.address.addressLocality,
          },
          senderMedium: sessionStorageValues.senderMedium,
          externalReference: sessionStorageValues.externalReference,
          externalReferenceSelect: sessionStorageValues.externalReferenceSelect,
          filename: sessionStorageValues.filename,
          description: sessionStorageValues.description,
          showInGallery: sessionStorageValues.showInGallery,
          sendEmailToCustomer: sessionStorageValues.sendEmailToCustomer,
          selectedMediaObjects: sessionStorageValues.selectedMediaObjects,
        })
      );
    }
  }, [initialValues]);

  return (
    <Box sx={casePaidTaskStyle.pdfGenerator}>
      <PdfGeneratorFormElements
        product={product}
        mediaObjects={mediaObjects?.["hydra:member"] || []}
        minimalView
      />
      <PdfGeneratorPreview
        product={product}
        values={formContext.getValues()}
        getParams={getParams}
        mediaObjects={mediaObjects || []}
      />
    </Box>
  );
};

export default CasePaidTaskPdfGenerator;
